import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Ban,
  CircleCheck,
  Crown,
  Pencil,
  Search,
  ShieldCheck,
  Users as UsersIcon,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input, Select } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/features/auth/useAuth'
import { UserAccessDialog } from '@/features/users/UserAccessDialog'
import { accessChangeBlockReason } from '@/features/users/permissions'
import { USERS_PAGE_LIMIT } from '@/features/users/users-api'
import { useUsers } from '@/features/users/useUsers'
import { PLAN_LABELS, ROLE_LABELS, isSuperAdminEmail } from '@/lib/constants'
import type { UserProfile } from '@/lib/types'
import { formatDateTime, toDate } from '@/lib/utils'

type RoleFilter = 'all' | 'admin' | 'user'
type StatusFilter = 'all' | 'active' | 'blocked'

/** Stage 2: the list of `users`, plus the only two fields the console may write — role and plan. */
export function UsersPage() {
  const { user: currentUser } = useAuth()
  const { users, isLoading, error } = useUsers()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [editing, setEditing] = useState<UserProfile | null>(null)

  // Search and sort on the client: the query is unordered on purpose (no Firestore index, and no
  // document is dropped for a missing field).
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return users
      .filter((item) => (roleFilter === 'all' ? true : item.role === roleFilter))
      .filter((item) =>
        statusFilter === 'all'
          ? true
          : statusFilter === 'active'
            ? item.isActive
            : !item.isActive,
      )
      .filter((item) =>
        needle.length === 0
          ? true
          : `${item.email ?? ''} ${item.displayName ?? ''} ${item.uid}`
              .toLowerCase()
              .includes(needle),
      )
      .sort(compareUsers)
  }, [users, search, roleFilter, statusFilter])

  const adminCount = users.filter((item) => item.role === 'admin').length
  const blockedCount = users.filter((item) => !item.isActive).length
  // The freshest state of the row being edited, so the dialog follows live updates.
  const editingUser = editing ? users.find((item) => item.uid === editing.uid) ?? editing : null

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Пользователи</h1>
          <p className="text-muted-foreground text-sm">
            {isLoading
              ? 'Загрузка…'
              : `Всего: ${users.length}${users.length >= USERS_PAGE_LIMIT ? '+' : ''} · администраторов: ${adminCount}${blockedCount > 0 ? ` · заблокировано: ${blockedCount}` : ''}`}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="relative min-w-56 flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по email, имени или UID"
              className="pl-9"
              aria-label="Поиск пользователей"
            />
          </div>
          <Select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value as RoleFilter)}
            className="w-40"
            aria-label="Фильтр по роли"
          >
            <option value="all">Все роли</option>
            <option value="admin">Администраторы</option>
            <option value="user">Пользователи</option>
          </Select>
          <Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className="w-44"
            aria-label="Фильтр по статусу"
          >
            <option value="all">Любой статус</option>
            <option value="active">Активные</option>
            <option value="blocked">Заблокированные</option>
          </Select>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Список не загружен</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="py-0">
        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[0, 1, 2, 3].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
              <UsersIcon className="size-8" />
              {users.length === 0 ? 'Пользователей нет.' : 'Ничего не найдено.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Пользователь</TableHead>
                  <TableHead>Роль</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Тариф</TableHead>
                  <TableHead>Регистрация</TableHead>
                  <TableHead className="text-right">Доступ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((item) => {
                  const blockReason = accessChangeBlockReason(item, currentUser?.uid)
                  const isSelf = item.uid === currentUser?.uid
                  return (
                    <TableRow key={item.uid}>
                      <TableCell>
                        <div className="flex min-w-0 flex-col">
                          <span className="flex items-center gap-2 truncate font-medium">
                            {item.email ?? '—'}
                            {isSuperAdminEmail(item.email) ? (
                              <Badge variant="outline" className="gap-1">
                                <Crown className="size-3" />
                                супер-админ
                              </Badge>
                            ) : null}
                            {isSelf ? <Badge variant="secondary">вы</Badge> : null}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {item.displayName ?? item.uid}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.role === 'admin' ? (
                          <Badge className="gap-1">
                            <ShieldCheck className="size-3" />
                            {ROLE_LABELS.admin}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">{ROLE_LABELS.user}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.isActive ? (
                          <Badge variant="outline" className="gap-1">
                            <CircleCheck className="size-3 text-emerald-500" />
                            Активен
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1">
                            <Ban className="size-3" />
                            Заблокирован
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.plan ? PLAN_LABELS[item.plan] : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                        {formatDateTime(item.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={blockReason !== null}
                          title={blockReason ?? 'Изменить роль или тариф'}
                          onClick={() => setEditing(item)}
                        >
                          <Pencil />
                          Изменить
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        Менять можно только роль, тариф и доступ, и только другому пользователю: свои изменить нельзя
        (защита от потери доступа), супер-администратор неприкосновенен. Это задано в правилах
        Firestore, а не в интерфейсе. Заблокированный пользователь видит в мобильном приложении экран
        «Пользователь заблокирован» и не может ничего записать в облако. Список загружается порциями
        до {USERS_PAGE_LIMIT} профилей.
      </p>

      {editingUser ? (
        <UserAccessDialog user={editingUser} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  )
}

/** Admins first, then by registration date (newest first), then by e-mail. */
function compareUsers(left: UserProfile, right: UserProfile): number {
  if (left.role !== right.role) return left.role === 'admin' ? -1 : 1
  const leftDate = toDate(left.createdAt)?.getTime() ?? 0
  const rightDate = toDate(right.createdAt)?.getTime() ?? 0
  if (leftDate !== rightDate) return rightDate - leftDate
  return (left.email ?? '').localeCompare(right.email ?? '')
}
